import { For, Show, createSignal } from "solid-js";

import { ScaiControlErrorReference } from "~/components/ScaiControlErrorReference";
import { useBulkSubscriptionAction } from "~/lib/hooks";
import type {
  BulkAction,
  BulkActionResponse,
  SubscriptionRead,
} from "~/lib/types";

type Props = {
  wsKey: () => string;
  selected: () => SubscriptionRead[];
  clearSelection: () => void;
};

const ACTION_LABELS: Record<BulkAction, string> = {
  cancel: "Cancel at period end",
  cancel_immediate: "Cancel immediately",
  suspend: "Pause",
  resume: "Resume",
};

/**
 * Sticky action bar shown when the rep has selected ≥1 subscription
 * row. Lets them apply one lifecycle action (cancel/cancel_immediate/
 * suspend/resume) to the entire selection in one POST.
 *
 * The result modal surfaces per-id outcomes — ScaiControl's bulk
 * endpoint is best-effort, so partial success is the common case for
 * batches across mixed-state subscriptions.
 */
export function BulkSubscriptionActionBar(props: Props) {
  const [action, setAction] = createSignal<BulkAction | "">("");
  const [reason, setReason] = createSignal("");
  const [confirming, setConfirming] = createSignal(false);
  const [result, setResult] = createSignal<BulkActionResponse | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [errorObj, setErrorObj] = createSignal<unknown>(null);

  const mutate = useBulkSubscriptionAction(props.wsKey);

  const submit = async () => {
    const chosen = action();
    if (!chosen) return;
    setError(null);
    setErrorObj(null);
    try {
      const response = await mutate.mutateAsync({
        subscription_ids: props.selected().map((s) => s.id),
        action: chosen,
        reason: reason().trim() || null,
      });
      setResult(response);
      setConfirming(false);
    } catch (err) {
      setError((err as Error).message);
      setErrorObj(err);
    }
  };

  const closeResult = () => {
    setResult(null);
    setAction("");
    setReason("");
    props.clearSelection();
  };

  return (
    <div class="sticky bottom-0 z-10 bg-base-200/95 border-t border-base-300 px-3 py-2 flex flex-wrap items-center gap-2">
      <span class="text-sm font-medium">
        {props.selected().length} selected
      </span>

      <select
        class="select select-bordered select-sm"
        value={action()}
        onChange={(e) => setAction(e.currentTarget.value as BulkAction | "")}
        aria-label="Bulk action"
      >
        <option value="">— pick an action —</option>
        <For each={Object.entries(ACTION_LABELS)}>
          {([key, label]) => <option value={key}>{label}</option>}
        </For>
      </select>

      <input
        type="text"
        class="input input-bordered input-sm flex-1 min-w-[12rem]"
        placeholder="Reason (optional)"
        value={reason()}
        onInput={(e) => setReason(e.currentTarget.value)}
        maxLength={500}
      />

      <button
        type="button"
        class="btn btn-primary btn-sm"
        disabled={!action() || mutate.isPending}
        onClick={() => setConfirming(true)}
      >
        Apply
      </button>

      <button
        type="button"
        class="btn btn-ghost btn-sm"
        onClick={props.clearSelection}
        disabled={mutate.isPending}
      >
        Clear
      </button>

      <Show when={confirming() && action()}>
        <dialog open class="modal modal-open" aria-modal="true">
          <div class="modal-box max-w-md">
            <h3 class="font-bold text-lg mb-2">
              {ACTION_LABELS[action() as BulkAction]} on{" "}
              {props.selected().length} subscriptions?
            </h3>
            <p class="text-sm opacity-70 mb-3">
              Submits one batch action to ScaiControl. Each subscription's
              state lands locally on its next webhook (~30s). Partial
              success is possible — the result modal will show per-id
              outcomes.
            </p>
            <ul class="text-xs opacity-70 mb-3 max-h-40 overflow-y-auto">
              <For each={props.selected()}>
                {(s) => (
                  <li>
                    <code>{s.scaicontrol_subscription_id}</code> · {s.plan_name}{" "}
                    ({s.state})
                  </li>
                )}
              </For>
            </ul>
            <Show when={error()}>
              <div
                role="alert"
                class="alert alert-error text-sm mb-3 flex flex-col items-start"
              >
                <span>{error()}</span>
                <ScaiControlErrorReference error={errorObj()} />
              </div>
            </Show>
            <div class="modal-action">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={() => setConfirming(false)}
                disabled={mutate.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                class={`btn btn-sm ${
                  action()?.includes("cancel") ? "btn-error" : "btn-primary"
                }`}
                onClick={submit}
                disabled={mutate.isPending}
              >
                {mutate.isPending ? "Submitting…" : "Confirm"}
              </button>
            </div>
          </div>
        </dialog>
      </Show>

      <Show when={result()}>
        {(r) => (
          <dialog open class="modal modal-open" aria-modal="true">
            <div class="modal-box max-w-2xl">
              <h3 class="font-bold text-lg mb-2">Bulk action result</h3>
              <p class="text-sm mb-2">{r().note}</p>

              <div class="stats stats-horizontal w-full text-sm shadow-sm mb-3">
                <div class="stat py-2">
                  <div class="stat-title">Total</div>
                  <div class="stat-value text-lg">{r().total}</div>
                </div>
                <div class="stat py-2">
                  <div class="stat-title">Succeeded</div>
                  <div class="stat-value text-lg text-success">
                    {r().succeeded_count}
                  </div>
                </div>
                <div class="stat py-2">
                  <div class="stat-title">Failed</div>
                  <div class="stat-value text-lg text-error">
                    {r().failed_count}
                  </div>
                </div>
              </div>

              <Show when={r().failed.length > 0}>
                <div class="mb-3">
                  <div class="text-xs uppercase opacity-50 mb-1">Failed</div>
                  <ul class="text-xs">
                    <For each={r().failed}>
                      {(item) => (
                        <li class="py-0.5">
                          <code>{item.scaicontrol_subscription_id}</code>
                          <Show when={item.code}>
                            <span class="ml-1 badge badge-error badge-xs">
                              {item.code}
                            </span>
                          </Show>
                          <Show when={item.error}>
                            <span class="ml-1 opacity-70">— {item.error}</span>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              <Show when={r().scaicontrol_request_id}>
                <p class="text-xs opacity-60 mt-2">
                  Reference: <code>{r().scaicontrol_request_id}</code>
                </p>
              </Show>

              <div class="modal-action">
                <button type="button" class="btn btn-primary btn-sm" onClick={closeResult}>
                  Close
                </button>
              </div>
            </div>
          </dialog>
        )}
      </Show>
    </div>
  );
}
